import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { Link } from "react-router-dom";
import {
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  DocumentTextIcon,
  UserGroupIcon,
  HomeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BanknotesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";
import { investorsService } from "../services/investorsService";
import { showPermissionAlert } from "../services/permissionService";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import BulkDeleteBySchemeModal from "../components/BulkDeleteBySchemeModal";
import InvestorForm from "../components/InvestorForm";
import investManag from "../assests/images/inv_manage.png";
import { useAuth } from "../context/AuthContext";


// Custom styles for sticky columns
const stickyStyles = `
  <style>
    .sticky-table {
      position: relative;
    }
    .sticky-table .sticky-left-0 {
      position: sticky;
      left: 0;
      box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
    }
    .sticky-table .sticky-left-16 {
      position: sticky;
      left: 4rem;
      box-shadow: 2px 0 4px rgba(0, 0, 0, 0.1);
    }
    .sticky-table .sticky-right-0 {
      position: sticky;
      right: 0;
      box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
    }
    .sticky-table thead th.sticky-left-0,
    .sticky-table thead th.sticky-left-16,
    .sticky-table thead th.sticky-right-0 {
      z-index: 30;
    }
    .sticky-table tbody td.sticky-left-0,
    .sticky-table tbody td.sticky-left-16,
    .sticky-table tbody td.sticky-right-0 {
      z-index: 20;
    }
    .sticky-table tr:hover td.sticky-left-0,
    .sticky-table tr:hover td.sticky-left-16,
    .sticky-table tr:hover td.sticky-right-0 {
      background-color: #f9fafb;
    }
  </style>
`;

const InvestorsPage = () => {
  // Get authentication and permission context
  const { getColumnPermissions, getUserRole, hasPermission } = useAuth();
  const columnPermissions = useMemo(() => getColumnPermissions(), [getColumnPermissions]);
  const userRole = getUserRole();

  // Permission checks for stats cards
  const canViewInvestorStats = useMemo(() => {
    // Admins can view all stats
    if (userRole === 'admin') return true;
    // Admin team needs status column permission to see investor count stats (Total, With UNS, Returned)
    return columnPermissions.status === true;
  }, [userRole, columnPermissions]);

  const canViewFinancialStats = useMemo(() => {
    // Admins can view all stats
    if (userRole === 'admin') return true;
    // Admin team needs usdt_received column permission to see financial stats
    return columnPermissions.usdt_received === true;
  }, [userRole, columnPermissions]);

  const [investors, setInvestors] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [selectedReferralInfo, setSelectedReferralInfo] = useState(null);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);

  // Search and filter states
  const [globalSearchTerm, setGlobalSearchTerm] = useState("");
  const [exactBuyerName, setExactBuyerName] = useState("");
  const [exactWalletAddress, setExactWalletAddress] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [schemeFilter, setSchemeFilter] = useState("ALL");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [debouncedExactBuyerName, setDebouncedExactBuyerName] = useState("");
  const [debouncedExactWalletAddress, setDebouncedExactWalletAddress] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Sorting states
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("DESC");

  // Table view states
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [visibleColumns, setVisibleColumns] = useState({
    // Always visible (sticky)
    id: true,
    buyer_name: true,
    actions: true,
    // Core investor info
    mobile_number: true,
    email: true,
    wallet_address: true,
    purchase_date: true,
    return_date: true,
    status: true,
    // Optional columns
    person_place: false,
    referral: false,
    token_number: false,
    scheme_name: true,
    additional_info: false,
    // Contract stacking columns
    transaction_hash: false,
    transaction_date: false,
    from_wallet: false,
    to_wallet: false,
    usdt_received: true,
    transaction_remarks: false,
  });

  // Check if a column can be displayed (has permission AND is toggled visible)
  const canShowColumn = useCallback((columnKey) => {
    // Always show id and buyer_name (these are core columns without permission control)
    if (['id', 'buyer_name'].includes(columnKey)) {
      return visibleColumns[columnKey];
    }
    // Admins have all permissions
    if (userRole === 'admin') {
      return visibleColumns[columnKey];
    }
    // For admin_team, column must have explicit permission AND be toggled visible
    const hasPermission = columnPermissions[columnKey] === true;
    return hasPermission && visibleColumns[columnKey];
  }, [columnPermissions, visibleColumns, userRole]);

  // Check if user has permission for a specific column
  const hasColumnPermission = useCallback((columnKey) => {
    // Always allow id and buyer_name
    if (['id', 'buyer_name'].includes(columnKey)) {
      return true;
    }
    // Admins have all permissions
    if (userRole === 'admin') {
      return true;
    }
    // For admin_team, must have explicit true permission
    return columnPermissions[columnKey] === true;
  }, [columnPermissions, userRole]);

  // Statistics state
  const [stats, setStats] = useState({
    totalInvestors: 0,
    withUNS: 0,
    returned: 0,
    totalUSDT: 0,
    returnedUSDT: 0,
    remainingUSDT: 0,
    averageInvestment: 0,
  });

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(globalSearchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [globalSearchTerm]);

  // Debounce exact buyer name
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedExactBuyerName(exactBuyerName);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [exactBuyerName]);

  // Debounce exact wallet address
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedExactWalletAddress(exactWalletAddress);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [exactWalletAddress]);

  // Reset to first page when search or filter changes
  useEffect(() => {
    if (dataLoaded) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, debouncedExactBuyerName, debouncedExactWalletAddress, statusFilter, schemeFilter, dataLoaded]);

  // Combined data and stats fetching with stable loading state
  const fetchAllData = useCallback(async () => {
    // Only show loading spinner on initial load or when no data exists
    if (!dataLoaded) {
      setLoading(true);
    }

    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        search: debouncedSearchTerm,
        exactBuyerName: debouncedExactBuyerName,
        exactWalletAddress: debouncedExactWalletAddress,
        status: statusFilter,
        sortBy,
        sortOrder,
      });

      // Add scheme filter if not "ALL"
      if (schemeFilter !== "ALL") {
        params.append("scheme_id", schemeFilter);
      }

      const statsParams = new URLSearchParams({
        search: debouncedSearchTerm,
        exactBuyerName: debouncedExactBuyerName,
        exactWalletAddress: debouncedExactWalletAddress,
        status: statusFilter,
      });

      // Add scheme filter to stats params if not "ALL"
      if (schemeFilter !== "ALL") {
        statsParams.append("scheme_id", schemeFilter);
      }

      // Fetch data and stats separately to handle 403 silently
      let data = [];
      let paginationData = {
        currentPage: 1,
        limit: pageSize,
        totalCount: 0,
        totalPages: 0,
        hasNextPage: false,
        hasPrevPage: false,
      };
      let statsData = {
        totalInvestors: 0,
        withUNS: 0,
        returned: 0,
        totalUSDT: 0,
        returnedUSDT: 0,
        remainingUSDT: 0,
        averageInvestment: 0,
      };

      // Fetch investors data
      try {
        const investorsResponse = await api.get(`/api/investors?${params}`);
        data = investorsResponse.data.data;
        paginationData = investorsResponse.data.pagination;
      } catch (error) {
        if (error.response?.status !== 403) {
          console.error("Error fetching investors:", error);
        }
        // Silently fail for 403 - show empty list
      }

      // Fetch stats separately
      try {
        const statsResponse = await api.get(
          `/api/investors/stats?${statsParams}`
        );
        statsData = statsResponse.data;
      } catch (error) {
        if (error.response?.status !== 403) {
          console.error("Error fetching stats:", error);
        }
        // Silently fail for 403 - show zeros
      }

      // Use React's automatic batching for better performance
      setInvestors(data);
      setPagination(paginationData);
      setStats(statsData);

      if (!dataLoaded) {
        setDataLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (!dataLoaded) {
        setDataLoaded(true);
      }
    } finally {
      if (loading) {
        setLoading(false);
      }
    }
  }, [
    currentPage,
    pageSize,
    debouncedSearchTerm,
    debouncedExactBuyerName,
    debouncedExactWalletAddress,
    statusFilter,
    schemeFilter,
    sortBy,
    sortOrder,
    dataLoaded,
    loading,
  ]);

  // Initial data fetch with optimized loading
  useEffect(() => {
    const initializeData = async () => {
      if (dataLoaded) return; // Prevent duplicate loads

      try {
        setLoading(true);

        // Fetch schemes with silent 403 handling
        try {
          const schemesResponse = await api.get("/api/schemes");
          setSchemes(schemesResponse.data);
        } catch (error) {
          if (error.response?.status !== 403) {
            console.error("Error fetching schemes:", error);
          }
          // Silently fail for 403 - show empty schemes
          setSchemes([]);
        }

        // Fetch investors data
        await fetchAllData();
      } catch (error) {
        console.error("Error initializing data:", error);
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    };

    initializeData();
  }, []); // Empty dependency array for initial load only

  // Fetch data when dependencies change (after initial load)
  useEffect(() => {
    if (dataLoaded) {
      fetchAllData();
    }
  }, [fetchAllData, dataLoaded]);

  const handleFormSubmit = async (formData) => {
    try {
      if (editingInvestor) {
        // Update existing investor
        await api.put(`/api/investors/${editingInvestor.id}`, formData);
      } else {
        // Create new investor
        await api.post("/api/investors", formData);
      }

      // Refresh data without flickering
      await fetchAllData();
      resetForm();
    } catch (error) {
      console.error("Error saving investor:", error);

      // Handle specific error messages from backend
      // Check for permission errors first
      if (
        !showPermissionAlert(
          error,
          editingInvestor ? "update investors" : "create investors"
        )
      ) {
        if (error.response?.data?.message) {
          const errorMessage = error.response.data.message;
          const errorField = error.response.data.field;

          if (errorField === "transaction_hash") {
            alert(`Transaction Hash Error: ${errorMessage}`);
          } else if (
            errorMessage.includes("Duplicate entry") ||
            errorMessage.includes("already exists")
          ) {
            alert(`Duplicate Data Error: ${errorMessage}`);
          } else {
            alert(`Error: ${errorMessage}`);
          }
        } else if (error.response?.status === 400) {
          alert(
            "Invalid data provided. Please check your input and try again."
          );
        } else if (error.response?.status === 404) {
          alert("Investor not found. Please refresh the page and try again.");
        } else {
          alert("Failed to save investor. Please try again.");
        }
      }
    }
  };

  const resetForm = () => {
    setEditingInvestor(null);
    setShowModal(false);
  };

  const handleEdit = (investor) => {
    setEditingInvestor(investor);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const investor = investors.find((inv) => inv.id === id);
    if (investor) {
      setRecordToDelete(investor);
      setShowDeleteModal(true);
    }
  };

  const handleSoftDelete = async (deleteReason) => {
    if (!recordToDelete) return;

    try {
      await investorsService.softDeleteInvestor(
        recordToDelete.id,
        deleteReason
      );
      fetchAllData();
    } catch (error) {
      console.error("Error soft deleting investor:", error);
    }
  };

  const handlePermanentDelete = async () => {
    if (!recordToDelete) return;

    try {
      await investorsService.deleteInvestor(recordToDelete.id);
      fetchAllData();
    } catch (error) {
      console.error("Error permanently deleting investor:", error);
    }
  };

  const handlePageChange = (newPage) => {
    if (
      newPage >= 1 &&
      newPage <= pagination.totalPages &&
      newPage !== currentPage
    ) {
      setCurrentPage(newPage);
    }
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(field);
      setSortOrder("DESC");
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleViewReferralInfo = useCallback((investor) => {
    const referralInfo = {
      investor_name: investor.buyer_name,
      referred_by: investor.referred_by,
      referrer_wallet: investor.referrer_wallet,
    };
    setSelectedReferralInfo(referralInfo);
    setShowReferralModal(true);
  }, []);

  // Toggle row expansion
  const toggleRowExpansion = useCallback(
    (investorId) => {
      const newExpanded = new Set(expandedRows);
      if (newExpanded.has(investorId)) {
        newExpanded.delete(investorId);
      } else {
        newExpanded.add(investorId);
      }
      setExpandedRows(newExpanded);
    },
    [expandedRows]
  );

  // Copy to clipboard function with enhanced feedback
  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);

      // Create a temporary toast notification
      const toast = document.createElement("div");
      toast.className =
        "fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-opacity duration-300";
      toast.innerHTML = `
        <div class="flex items-center space-x-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
          </svg>
          <span>Copied to clipboard!</span>
        </div>
      `;
      document.body.appendChild(toast);

      // Remove the toast after 2 seconds
      setTimeout(() => {
        toast.style.opacity = "0";
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        alert("Copied to clipboard!");
      } catch (fallbackErr) {
        console.error("Fallback copy failed:", fallbackErr);
        alert("Failed to copy to clipboard");
      }
      document.body.removeChild(textArea);
    }
  }, []);

  // Toggle column visibility
  const toggleColumnVisibility = useCallback((columnKey) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  }, []);

  // Preset column combinations
  const applyColumnPreset = useCallback((preset) => {
    const presets = {
      essential: {
        person_place: false,
        referral: false,
        token_number: false,
        scheme_name: true,
        additional_info: false,
        transaction_hash: false,
        transaction_date: false,
        from_wallet: false,
        to_wallet: false,
        usdt_received: true,
        transaction_remarks: false,
      },
      complete: {
        person_place: true,
        referral: true,
        token_number: true,
        scheme_name: true,
        additional_info: true,
        transaction_hash: true,
        transaction_date: true,
        from_wallet: true,
        to_wallet: true,
        usdt_received: true,
        transaction_remarks: true,
      },
      transactions: {
        person_place: false,
        referral: false,
        token_number: false,
        scheme_name: false,
        additional_info: false,
        transaction_hash: true,
        transaction_date: true,
        from_wallet: true,
        to_wallet: true,
        usdt_received: true,
        transaction_remarks: true,
      },
    };

    setVisibleColumns((prev) => ({
      ...prev,
      ...presets[preset],
    }));
  }, []);

  // Memoize pagination info to prevent unnecessary re-renders
  const paginationInfo = useMemo(
    () => ({
      showingFrom: (pagination.currentPage - 1) * pagination.limit + 1,
      showingTo: Math.min(
        pagination.currentPage * pagination.limit,
        pagination.totalCount
      ),
      totalCount: pagination.totalCount,
      currentPage: pagination.currentPage,
      totalPages: pagination.totalPages,
    }),
    [pagination]
  );

  // Memoize sort indicator
  const getSortIndicator = useCallback(
    (field) => {
      if (sortBy === field) {
        return sortOrder === "ASC" ? "↑" : "↓";
      }
      return null;
    },
    [sortBy, sortOrder]
  );

  // Show loading only on initial load to prevent flickering
  if (loading && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600 text-lg">
            Loading Investors Management...
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Inject custom styles */}
      <div dangerouslySetInnerHTML={{ __html: stickyStyles }} />

      <div className="p-6">
        {/* Enhanced Navigation Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <HomeIcon className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>•</span>
                <span>Investors Management</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Bulk Delete by Scheme
              </button>
              <Link
                to="/deleted-records"
                className="inline-flex items-center px-4 py-2 border border-purple-300 shadow-sm text-sm font-medium rounded-md text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
              >
                <ArchiveBoxIcon className="h-4 w-4 mr-2" />
                Deleted Records
              </Link>
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <PlusIcon className="h-5 w-5" />
                Add Investor
              </button>
            </div>
          </div>

          {/* Page Title and Description */}
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-2">
              {/* <UserGroupIcon className="h-8 w-8 text-gray-700" /> */}
              <img
                  src={investManag}
                  alt="Investors Management Logo"
                  className="w-16 rounded-lg object-cover"
                />
              <h1 className="text-3xl font-bold text-gray-900">
                Investors Management
              </h1>
            </div>
            <p className="text-gray-600 max-w-3xl">
              Manage and track all investor records, including personal
              information, investment details, and transaction history. Use
              filters and search to quickly find specific investors.
            </p>
          </div>
        </div>

        {/* Statistics Cards - Investor Count Cards (requires investors.column.status permission) */}
        {canViewInvestorStats && (
          <div className={`grid grid-cols-1 md:grid-cols-2 ${canViewFinancialStats ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-8`}>
            {/* Investor Count Cards */}
            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                  <UserGroupIcon className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 mb-1">
                    {stats.totalInvestors.toLocaleString()}
                  </p>
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Total Investors
                  </h3>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-center">
                  <ClockIcon className="w-5 h-5 text-amber-600" />
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 mb-1">
                    {stats.withUNS.toLocaleString()}
                  </p>
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    With UNS
                  </h3>
                  <div className="mt-2 inline-block bg-gray-100 px-3 py-1 rounded-full">
                    <p className="text-xs font-medium text-gray-700">
                      {stats.totalInvestors > 0
                        ? `${(
                            (stats.withUNS / stats.totalInvestors) *
                            100
                          ).toFixed(1)}%`
                        : "0%"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900 mb-1">
                    {stats.returned.toLocaleString()}
                  </p>
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                    Returned
                  </h3>
                  <div className="mt-2 inline-block bg-gray-100 px-3 py-1 rounded-full">
                    <p className="text-xs font-medium text-gray-700">
                      {stats.totalInvestors > 0
                        ? `${(
                            (stats.returned / stats.totalInvestors) *
                            100
                          ).toFixed(1)}%`
                        : "0%"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Avg Investment Card - requires financial permission */}
            {canViewFinancialStats && (
              <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 group">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 bg-indigo-50 border border-indigo-200 rounded-lg flex items-center justify-center">
                    <BanknotesIcon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900 mb-1">
                      $
                      {stats.averageInvestment.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                    </p>
                    <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                      Avg Investment
                    </h3>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* USDT Financial Cards - requires usdt_received column permission */}
        {canViewFinancialStats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-center mr-4">
                  <CurrencyDollarIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Total USDT
                  </h3>
                  <p className="text-2xl font-bold text-gray-900">
                    $
                    {stats.totalUSDT.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-100 pt-4">
                <p className="text-sm font-medium text-gray-600">
                  Total Investment Volume
                </p>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center mr-4">
                  <ArrowTrendingUpIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Returned USDT
                  </h3>
                  <p className="text-2xl font-bold text-gray-900">
                    $
                    {stats.returnedUSDT.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                <p className="text-sm font-medium text-gray-600">
                  Completed Returns
                </p>
                <div className="bg-gray-100 px-3 py-1 rounded-full">
                  <p className="text-xs font-medium text-gray-700">
                    {stats.totalUSDT > 0
                      ? `${((stats.returnedUSDT / stats.totalUSDT) * 100).toFixed(
                          1
                        )}%`
                      : "0%"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-100 rounded-xl shadow-md hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-center mr-4">
                  <ArrowTrendingDownIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-1">
                    Remaining USDT
                  </h3>
                  <p className="text-2xl font-bold text-gray-900">
                    $
                    {stats.remainingUSDT.toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-100 pt-4 flex items-center justify-between">
                <p className="text-sm font-medium text-gray-600">
                  Pending Returns
                </p>
                <div className="bg-gray-100 px-3 py-1 rounded-full">
                  <p className="text-xs font-medium text-gray-700">
                    {stats.totalUSDT > 0
                      ? `${(
                          (stats.remainingUSDT / stats.totalUSDT) *
                          100
                        ).toFixed(1)}%`
                      : "0%"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter Controls */}
        <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col gap-4">
            {/* Active Filter Indicator */}
            {exactBuyerName && (
              <div className="bg-purple-100 border-l-4 border-purple-500 p-4 rounded-r-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-purple-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-purple-700 font-medium">
                      Filtering by exact buyer name:{" "}
                      <span className="font-bold">"{exactBuyerName}"</span>
                    </p>
                  </div>
                  <button
                    onClick={() => setExactBuyerName("")}
                    className="text-purple-600 hover:text-purple-800 font-medium text-sm underline"
                  >
                    Clear Filter
                  </button>
                </div>
              </div>
            )}
            {exactWalletAddress && (
              <div className="bg-teal-100 border-l-4 border-teal-500 p-4 rounded-r-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-teal-600 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-teal-700 font-medium">
                      Filtering by exact wallet address:{" "}
                      <span className="font-bold font-mono break-all">"{exactWalletAddress}"</span>
                    </p>
                  </div>
                  <button
                    onClick={() => setExactWalletAddress("")}
                    className="text-teal-600 hover:text-teal-800 font-medium text-sm underline"
                  >
                    Clear Filter
                  </button>
                </div>
              </div>
            )}

            {/* Search and Status Filter Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search Investors
                  <span className="ml-2 text-xs text-gray-500">
                    (Partial match)
                  </span>
                </label>
                <input
                  type="text"
                  placeholder="Search by name, mobile, wallet address, etc..."
                  value={globalSearchTerm}
                  onChange={(e) => setGlobalSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!!exactBuyerName}
                />
                {exactBuyerName && (
                  <p className="mt-1 text-xs text-gray-500">
                    Global search is disabled when exact buyer name filter is
                    active
                  </p>
                )}
              </div>
              <div className="sm:w-64">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exact Buyer Name
                  <span className="ml-2 text-xs text-gray-500">
                    (Exact match only)
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Enter exact buyer name..."
                    value={exactBuyerName}
                    onChange={(e) => setExactBuyerName(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-purple-50"
                  />
                  {exactBuyerName && (
                    <button
                      onClick={() => setExactBuyerName("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Clear exact buyer name filter"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="sm:w-64">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Exact Wallet Address
                  <span className="ml-2 text-xs text-gray-500">
                    (Exact match only)
                  </span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Enter exact wallet address..."
                    value={exactWalletAddress}
                    onChange={(e) => setExactWalletAddress(e.target.value)}
                    className="w-full px-4 py-2 pr-10 border border-teal-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-teal-50 font-mono text-sm"
                  />
                  {exactWalletAddress && (
                    <button
                      onClick={() => setExactWalletAddress("")}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      title="Clear exact wallet address filter"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <div className="sm:w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ALL">All Status</option>
                  <option value="With UNS">With UNS</option>
                  <option value="Returned">Returned</option>
                </select>
              </div>
              <div className="sm:w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Scheme
                </label>
                <select
                  value={schemeFilter}
                  onChange={(e) => setSchemeFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="ALL">All Schemes</option>
                  {schemes.map((scheme) => (
                    <option key={scheme.id} value={scheme.id}>
                      {scheme.name} ({scheme.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:w-40">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Per Page
                </label>
                <select
                  value={pageSize}
                  onChange={(e) =>
                    handlePageSizeChange(parseInt(e.target.value))
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>

            {/* Column Visibility Controls */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Show/Hide Columns
                  </label>
                  {userRole === "admin_team" && (
                    <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                      <LockClosedIcon className="h-3 w-3 mr-1" />
                      Some columns restricted
                    </span>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => applyColumnPreset("essential")}
                    className="px-3 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50"
                  >
                    Essential View
                  </button>
                  <button
                    onClick={() => applyColumnPreset("transactions")}
                    className="px-3 py-1 text-xs font-medium text-green-600 border border-green-200 rounded-md hover:bg-green-50"
                  >
                    Transaction Focus
                  </button>
                  <button
                    onClick={() => applyColumnPreset("complete")}
                    className="px-3 py-1 text-xs font-medium text-purple-600 border border-purple-200 rounded-md hover:bg-purple-50"
                  >
                    Complete View
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "person_place", label: "Place" },
                  { key: "referral", label: "Referral" },
                  { key: "token_number", label: "Token Number" },
                  { key: "scheme_name", label: "Scheme" },
                  { key: "additional_info", label: "Additional Info" },
                  { key: "transaction_hash", label: "Transaction Hash" },
                  { key: "transaction_date", label: "Transaction Date" },
                  { key: "from_wallet", label: "From Wallet" },
                  { key: "to_wallet", label: "To Wallet" },
                  { key: "usdt_received", label: "USDT Received" },
                  { key: "transaction_remarks", label: "Transaction Remarks" },
                ].map((column) => {
                  const hasPermission = hasColumnPermission(column.key);
                  return (
                    <button
                      key={column.key}
                      onClick={() => hasPermission && toggleColumnVisibility(column.key)}
                      disabled={!hasPermission}
                      className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors inline-flex items-center ${
                        !hasPermission
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : visibleColumns[column.key]
                          ? "bg-blue-50 text-blue-700 border-blue-200"
                          : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                      }`}
                      title={!hasPermission ? "You don't have permission to view this column" : ""}
                    >
                      {!hasPermission && <LockClosedIcon className="h-3 w-3 mr-1" />}
                      {column.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Investors Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden relative">
          {loading && dataLoaded && (
            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
              <div className="flex items-center space-x-2 bg-white rounded-lg shadow-lg px-4 py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-gray-600 text-sm">Updating...</span>
              </div>
            </div>
          )}

          {/* Table Header with Results Info */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-700">
                Showing {paginationInfo.showingFrom} to{" "}
                {paginationInfo.showingTo} of {paginationInfo.totalCount}{" "}
                results
              </div>
              <div className="text-sm text-gray-500">
                Page {paginationInfo.currentPage} of {paginationInfo.totalPages}
              </div>
            </div>
          </div>

          {/* Modern Table with Sticky Columns */}
          <div className="overflow-hidden sticky-table">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-20">
                  <tr>
                    {/* Sticky Left Columns */}
                    <th className="sticky left-0 z-30 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                      <div className="flex items-center">
                        <button
                          onClick={() => handleSortChange("id")}
                          className="flex items-center hover:text-gray-700"
                        >
                          ID
                          {getSortIndicator("id") && (
                            <span className="ml-1">
                              {getSortIndicator("id")}
                            </span>
                          )}
                        </button>
                      </div>
                    </th>
                    <th className="sticky left-16 z-30 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200 min-w-48">
                      <div className="flex items-center">
                        <button
                          onClick={() => handleSortChange("buyer_name")}
                          className="flex items-center hover:text-gray-700"
                        >
                          Buyer Name
                          {getSortIndicator("buyer_name") && (
                            <span className="ml-1">
                              {getSortIndicator("buyer_name")}
                            </span>
                          )}
                        </button>
                      </div>
                    </th>

                    {/* Scrollable Columns */}
                    {canShowColumn('mobile_number') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Mobile
                      </th>
                    )}
                    {canShowColumn('email') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                    )}
                    {canShowColumn('wallet_address') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Wallet Address
                      </th>
                    )}
                    {canShowColumn('purchase_date') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSortChange("purchase_date")}
                          className="flex items-center hover:text-gray-700"
                        >
                          Purchase Date
                          {getSortIndicator("purchase_date") && (
                            <span className="ml-1">
                              {getSortIndicator("purchase_date")}
                            </span>
                          )}
                        </button>
                      </th>
                    )}
                    {canShowColumn('usdt_received') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSortChange("usdt_received")}
                          className="flex items-center hover:text-gray-700"
                        >
                          USDT Received
                          {getSortIndicator("usdt_received") && (
                            <span className="ml-1">
                              {getSortIndicator("usdt_received")}
                            </span>
                          )}
                        </button>
                      </th>
                    )}
                    {canShowColumn('token_number') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Token Number
                      </th>
                    )}
                    {canShowColumn('return_date') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSortChange("return_date")}
                          className="flex items-center hover:text-gray-700"
                        >
                          Return Date
                          {getSortIndicator("return_date") && (
                            <span className="ml-1">
                              {getSortIndicator("return_date")}
                            </span>
                          )}
                        </button>
                      </th>
                    )}
                    {canShowColumn('status') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => handleSortChange("status")}
                          className="flex items-center hover:text-gray-700"
                        >
                          Status
                          {getSortIndicator("status") && (
                            <span className="ml-1">
                              {getSortIndicator("status")}
                            </span>
                          )}
                        </button>
                      </th>
                    )}

                    {/* Conditional Columns */}
                    {canShowColumn('person_place') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Place
                      </th>
                    )}
                    {canShowColumn('referral') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Referral
                      </th>
                    )}
                    {canShowColumn('scheme_name') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Scheme
                      </th>
                    )}
                    {canShowColumn('additional_info') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Additional Info
                      </th>
                    )}
                    {canShowColumn('transaction_hash') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction Hash
                      </th>
                    )}
                    {canShowColumn('transaction_date') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction Date
                      </th>
                    )}
                    {canShowColumn('from_wallet') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        From Wallet
                      </th>
                    )}
                    {canShowColumn('to_wallet') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        To Wallet
                      </th>
                    )}
                    {canShowColumn('transaction_remarks') && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transaction Remarks
                      </th>
                    )}

                    {/* Sticky Right Actions Column */}
                    {canShowColumn('actions') && (
                      <th className="sticky right-0 z-30 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-l border-gray-200">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {investors.map((investor) => (
                    <React.Fragment key={investor.id}>
                      {/* Main Row */}
                      <tr className="hover:bg-gray-50">
                        {/* Sticky Left Columns */}
                        <td className="sticky left-0 z-20 bg-white px-6 py-4 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
                          <div className="flex items-center">
                            <button
                              onClick={() => toggleRowExpansion(investor.id)}
                              className="mr-2 text-gray-400 hover:text-gray-600"
                            >
                              {expandedRows.has(investor.id) ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                            {investor.id}
                          </div>
                        </td>
                        <td className="sticky left-16 z-20 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200">
                          {investor.buyer_name}
                        </td>

                        {/* Scrollable Columns */}
                        {canShowColumn('mobile_number') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.mobile_number}
                          </td>
                        )}
                        {canShowColumn('email') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.email || "-"}
                          </td>
                        )}
                        {canShowColumn('wallet_address') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                            <div className="flex items-center space-x-2">
                              <span title={investor.wallet_address}>
                                {investor.wallet_address?.substring(0, 10)}...
                              </span>
                              <button
                                onClick={() =>
                                  copyToClipboard(investor.wallet_address)
                                }
                                className="text-blue-600 hover:text-blue-800 p-1"
                                title="Copy wallet address"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                  />
                                </svg>
                              </button>
                            </div>
                          </td>
                        )}
                        {canShowColumn('purchase_date') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.purchase_date}
                          </td>
                        )}
                        {canShowColumn('usdt_received') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.usdt_received || "N/A"}
                          </td>
                        )}
                        {canShowColumn('token_number') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.token_number || "N/A"}
                          </td>
                        )}
                        {canShowColumn('return_date') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.return_date || "N/A"}
                          </td>
                        )}
                        {canShowColumn('status') && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                investor.status === "With UNS"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {investor.status}
                            </span>
                          </td>
                        )}

                        {/* Conditional Columns */}
                        {canShowColumn('person_place') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.person_place}
                          </td>
                        )}
                        {canShowColumn('referral') && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {investor.referred_by ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() =>
                                    handleViewReferralInfo(investor)
                                  }
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 hover:text-indigo-800 hover:border-indigo-300 transition-all duration-200 backdrop-blur-sm"
                                >
                                  <svg
                                    className="w-3 h-3 mr-1.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                  View Details
                                </button>
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      `Referred by: ${
                                        investor.referred_by
                                      }\nReferrer Wallet: ${
                                        investor.referrer_wallet || "N/A"
                                      }`
                                    )
                                  }
                                  className="text-gray-400 hover:text-indigo-600 p-1.5 rounded-lg hover:bg-indigo-50 transition-colors duration-200"
                                  title="Copy referral details to clipboard"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-lg">
                                <svg
                                  className="w-3 h-3 mr-1.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L5.636 5.636"
                                  />
                                </svg>
                                No Referral
                              </span>
                            )}
                          </td>
                        )}
                        {canShowColumn('scheme_name') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.scheme_name}
                          </td>
                        )}
                        {canShowColumn('additional_info') && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              try {
                                // Handle both string and object formats for dynamic_fields
                                let dynamicFields = investor.dynamic_fields;

                                // If it's a string, try to parse it as JSON
                                if (typeof dynamicFields === "string") {
                                  if (
                                    dynamicFields.trim() === "" ||
                                    dynamicFields === "{}"
                                  ) {
                                    dynamicFields = {};
                                  } else {
                                    dynamicFields = JSON.parse(dynamicFields);
                                  }
                                }

                                // If it's null/undefined, default to empty object
                                if (!dynamicFields) {
                                  dynamicFields = {};
                                }

                                const fieldCount =
                                  typeof dynamicFields === "object"
                                    ? Object.keys(dynamicFields).length
                                    : 0;

                                return fieldCount > 0 ? (
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() =>
                                        toggleRowExpansion(investor.id)
                                      }
                                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 hover:text-emerald-800 hover:border-emerald-300 transition-all duration-200 backdrop-blur-sm"
                                    >
                                      <svg
                                        className="w-3 h-3 mr-1.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                      </svg>
                                      View Fields ({fieldCount})
                                    </button>
                                    <button
                                      onClick={() => {
                                        const fieldsText = Object.entries(
                                          dynamicFields
                                        )
                                          .map(
                                            ([key, value]) => `${key}: ${value}`
                                          )
                                          .join("\n");
                                        copyToClipboard(fieldsText);
                                      }}
                                      className="text-gray-400 hover:text-emerald-600 p-1.5 rounded-lg hover:bg-emerald-50 transition-colors duration-200"
                                      title="Copy additional info to clipboard"
                                    >
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                ) : (
                                  <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-lg">
                                    <svg
                                      className="w-3 h-3 mr-1.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                      />
                                    </svg>
                                    No additional info
                                  </span>
                                );
                              } catch (e) {
                                return (
                                  <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-500 bg-red-50 border border-red-200 rounded-lg">
                                    <svg
                                      className="w-3 h-3 mr-1.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                      />
                                    </svg>
                                    Invalid data
                                  </span>
                                );
                              }
                            })()}
                          </td>
                        )}
                        {canShowColumn('transaction_hash') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                            {investor.transaction_hash ? (
                              <div className="flex items-center space-x-2">
                                <span title={investor.transaction_hash}>
                                  {investor.transaction_hash.substring(0, 10) +
                                    "..."}
                                </span>
                                <button
                                  onClick={() =>
                                    copyToClipboard(investor.transaction_hash)
                                  }
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Copy transaction hash"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        )}
                        {canShowColumn('transaction_date') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.transaction_date || "N/A"}
                          </td>
                        )}
                        {canShowColumn('from_wallet') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                            {investor.from_wallet_address ? (
                              <div className="flex items-center space-x-2">
                                <span title={investor.from_wallet_address}>
                                  {investor.from_wallet_address.substring(
                                    0,
                                    10
                                  ) + "..."}
                                </span>
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      investor.from_wallet_address
                                    )
                                  }
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Copy from wallet address"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        )}
                        {canShowColumn('to_wallet') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                            {investor.to_wallet_address ? (
                              <div className="flex items-center space-x-2">
                                <span title={investor.to_wallet_address}>
                                  {investor.to_wallet_address.substring(0, 10) +
                                    "..."}
                                </span>
                                <button
                                  onClick={() =>
                                    copyToClipboard(investor.to_wallet_address)
                                  }
                                  className="text-blue-600 hover:text-blue-800 p-1"
                                  title="Copy to wallet address"
                                >
                                  <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                </button>
                              </div>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        )}
                        {canShowColumn('transaction_remarks') && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investor.transaction_remarks ? (
                              <span title={investor.transaction_remarks}>
                                {investor.transaction_remarks.length > 20
                                  ? investor.transaction_remarks.substring(
                                      0,
                                      20
                                    ) + "..."
                                  : investor.transaction_remarks}
                              </span>
                            ) : (
                              "N/A"
                            )}
                          </td>
                        )}

                        {/* Sticky Right Actions Column */}
                        {canShowColumn('actions') && (
                          <td className="sticky right-0 z-20 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium border-l border-gray-200">
                            <div className="flex space-x-2">
                              <Link
                                to={`/investors/${investor.id}/history`}
                                className="text-blue-600 hover:text-blue-900"
                                title="View History Dashboard"
                              >
                                <ChartBarIcon className="h-4 w-4" />
                              </Link>
                              <button
                                onClick={() => handleEdit(investor)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="Edit"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(investor.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>

                      {/* Expanded Row Details */}
                      {expandedRows.has(investor.id) && (
                        <tr className="bg-gradient-to-r from-gray-50 to-blue-50 border-l-4 border-blue-400">
                          <td colSpan="100%" className="px-6 py-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                              {/* Personal Information Section */}
                              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center mb-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                    <svg
                                      className="w-4 h-4 text-blue-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                      />
                                    </svg>
                                  </div>
                                  <h4 className="font-semibold text-gray-900 text-sm">
                                    Personal Information
                                  </h4>
                                </div>
                                <div className="space-y-3 text-sm">
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-600">
                                      Mobile:
                                    </span>
                                    <span className="text-gray-900">
                                      {investor.mobile_number}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-600">
                                      Email:
                                    </span>
                                    <span className="text-gray-900">
                                      {investor.email || "-"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-600">
                                      Place:
                                    </span>
                                    <span className="text-gray-900">
                                      {investor.person_place}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="font-medium text-gray-600">
                                      Wallet:
                                    </span>
                                    <div className="flex items-center justify-between mt-1 bg-gray-50 p-2 rounded-md">
                                      <span className="font-mono text-xs text-gray-700 break-all">
                                        {investor.wallet_address}
                                      </span>
                                      <button
                                        onClick={() =>
                                          copyToClipboard(
                                            investor.wallet_address
                                          )
                                        }
                                        className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-100 rounded transition-colors duration-200 ml-2"
                                        title="Copy wallet address"
                                      >
                                        <svg
                                          className="w-4 h-4"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Investment Details Section */}
                              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center mb-3">
                                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                                    <svg
                                      className="w-4 h-4 text-green-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                      />
                                    </svg>
                                  </div>
                                  <h4 className="font-semibold text-gray-900 text-sm">
                                    Investment Details
                                  </h4>
                                </div>
                                <div className="space-y-3 text-sm">
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-600">
                                      Token Number:
                                    </span>
                                    <span className="text-gray-900">
                                      {investor.token_number || "N/A"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-600">
                                      Scheme:
                                    </span>
                                    <span className="text-gray-900">
                                      {investor.scheme_name}
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="font-medium text-gray-600">
                                      Status:
                                    </span>
                                    <span
                                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                        investor.status === "With UNS"
                                          ? "bg-yellow-100 text-yellow-800"
                                          : "bg-green-100 text-green-800"
                                      }`}
                                    >
                                      {investor.status}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Transaction Details Section */}
                              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center mb-3">
                                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                                    <svg
                                      className="w-4 h-4 text-purple-600"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                      />
                                    </svg>
                                  </div>
                                  <h4 className="font-semibold text-gray-900 text-sm">
                                    Transaction Details
                                  </h4>
                                </div>
                                <div className="space-y-3 text-sm">
                                  <div>
                                    <span className="font-medium text-gray-600">
                                      Transaction Hash:
                                    </span>
                                    {investor.transaction_hash ? (
                                      <div className="flex items-center justify-between mt-1 bg-gray-50 p-2 rounded-md">
                                        <span className="font-mono text-xs text-gray-700 break-all">
                                          {investor.transaction_hash}
                                        </span>
                                        <button
                                          onClick={() =>
                                            copyToClipboard(
                                              investor.transaction_hash
                                            )
                                          }
                                          className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-100 rounded transition-colors duration-200 ml-2"
                                          title="Copy transaction hash"
                                        >
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-gray-500 text-xs ml-1">
                                        N/A
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-600">
                                      Transaction Date:
                                    </span>
                                    <span className="text-gray-900">
                                      {investor.transaction_date || "N/A"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="font-medium text-gray-600">
                                      USDT Received:
                                    </span>
                                    <span className="text-gray-900 font-mono">
                                      {investor.usdt_received || "N/A"}
                                    </span>
                                  </div>
                                  {investor.from_wallet_address && (
                                    <div>
                                      <span className="font-medium text-gray-600">
                                        From Wallet:
                                      </span>
                                      <div className="flex items-center justify-between mt-1 bg-gray-50 p-2 rounded-md">
                                        <span className="font-mono text-xs text-gray-700 break-all">
                                          {investor.from_wallet_address}
                                        </span>
                                        <button
                                          onClick={() =>
                                            copyToClipboard(
                                              investor.from_wallet_address
                                            )
                                          }
                                          className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-100 rounded transition-colors duration-200 ml-2"
                                          title="Copy from wallet address"
                                        >
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  {investor.to_wallet_address && (
                                    <div>
                                      <span className="font-medium text-gray-600">
                                        To Wallet:
                                      </span>
                                      <div className="flex items-center justify-between mt-1 bg-gray-50 p-2 rounded-md">
                                        <span className="font-mono text-xs text-gray-700 break-all">
                                          {investor.to_wallet_address}
                                        </span>
                                        <button
                                          onClick={() =>
                                            copyToClipboard(
                                              investor.to_wallet_address
                                            )
                                          }
                                          className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-100 rounded transition-colors duration-200 ml-2"
                                          title="Copy to wallet address"
                                        >
                                          <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                          >
                                            <path
                                              strokeLinecap="round"
                                              strokeLinejoin="round"
                                              strokeWidth="2"
                                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  {investor.transaction_remarks && (
                                    <div>
                                      <span className="font-medium text-gray-600">
                                        Remarks:
                                      </span>
                                      <p className="text-gray-900 mt-1 text-xs">
                                        {investor.transaction_remarks}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Enhanced Referral Information Section */}
                              {investor.referred_by && (
                                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-4 shadow-sm border-2 border-indigo-200">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center">
                                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                                        <svg
                                          className="w-4 h-4 text-indigo-600"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                          />
                                        </svg>
                                      </div>
                                      <h4 className="font-semibold text-indigo-900 text-sm">
                                        Referral Information
                                      </h4>
                                    </div>
                                    <button
                                      onClick={() =>
                                        copyToClipboard(
                                          `Referred by: ${
                                            investor.referred_by
                                          }\nReferrer Wallet: ${
                                            investor.referrer_wallet || "N/A"
                                          }`
                                        )
                                      }
                                      className="text-indigo-600 hover:text-indigo-800 p-2 hover:bg-indigo-100 rounded-lg transition-colors duration-200"
                                      title="Copy all referral details"
                                    >
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                        />
                                      </svg>
                                    </button>
                                  </div>
                                  <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                      <span className="font-medium text-indigo-700">
                                        Referred By:
                                      </span>
                                      <span className="text-indigo-900 font-semibold">
                                        {investor.referred_by}
                                      </span>
                                    </div>
                                    {investor.referrer_wallet && (
                                      <div>
                                        <span className="font-medium text-indigo-700">
                                          Referrer Wallet:
                                        </span>
                                        <div className="flex items-center justify-between mt-1 bg-white p-2 rounded-md border border-indigo-200">
                                          <span className="font-mono text-xs text-indigo-800 break-all">
                                            {investor.referrer_wallet}
                                          </span>
                                          <button
                                            onClick={() =>
                                              copyToClipboard(
                                                investor.referrer_wallet
                                              )
                                            }
                                            className="text-indigo-600 hover:text-indigo-800 p-1 hover:bg-indigo-100 rounded transition-colors duration-200 ml-2"
                                            title="Copy referrer wallet address"
                                          >
                                            <svg
                                              className="w-4 h-4"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Enhanced Additional Information Section */}
                              {(() => {
                                try {
                                  // Handle both string and object formats for dynamic_fields
                                  let dynamicFields = investor.dynamic_fields;

                                  // If it's a string, try to parse it as JSON
                                  if (typeof dynamicFields === "string") {
                                    if (
                                      dynamicFields.trim() === "" ||
                                      dynamicFields === "{}"
                                    ) {
                                      dynamicFields = {};
                                    } else {
                                      dynamicFields = JSON.parse(dynamicFields);
                                    }
                                  }

                                  // If it's null/undefined, default to empty object
                                  if (!dynamicFields) {
                                    dynamicFields = {};
                                  }

                                  const hasFields =
                                    typeof dynamicFields === "object" &&
                                    Object.keys(dynamicFields).length > 0;

                                  return (
                                    hasFields && (
                                      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-4 shadow-sm border-2 border-emerald-200">
                                        <div className="flex items-center justify-between mb-3">
                                          <div className="flex items-center">
                                            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                                              <svg
                                                className="w-4 h-4 text-emerald-600"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2"
                                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                              </svg>
                                            </div>
                                            <h4 className="font-semibold text-emerald-900 text-sm">
                                              Additional Information
                                            </h4>
                                          </div>
                                          <button
                                            onClick={() => {
                                              const fieldsText = Object.entries(
                                                dynamicFields
                                              )
                                                .map(
                                                  ([key, value]) =>
                                                    `${key}: ${value}`
                                                )
                                                .join("\n");
                                              copyToClipboard(fieldsText);
                                            }}
                                            className="text-emerald-600 hover:text-emerald-800 p-2 hover:bg-emerald-100 rounded-lg transition-colors duration-200"
                                            title="Copy all additional information"
                                          >
                                            <svg
                                              className="w-4 h-4"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                              />
                                            </svg>
                                          </button>
                                        </div>
                                        <div className="space-y-3 text-sm">
                                          {Object.entries(dynamicFields).map(
                                            ([key, value]) => (
                                              <div
                                                key={key}
                                                className="flex justify-between items-center bg-white p-2 rounded-md border border-emerald-200"
                                              >
                                                <span className="font-medium text-emerald-700 capitalize">
                                                  {key.replace(/_/g, " ")}:
                                                </span>
                                                <div className="flex items-center space-x-2">
                                                  <span className="text-emerald-900 text-right">
                                                    {value}
                                                  </span>
                                                  <button
                                                    onClick={() =>
                                                      copyToClipboard(
                                                        `${key}: ${value}`
                                                      )
                                                    }
                                                    className="text-emerald-600 hover:text-emerald-800 p-1 hover:bg-emerald-100 rounded transition-colors duration-200"
                                                    title={`Copy ${key}`}
                                                  >
                                                    <svg
                                                      className="w-3 h-3"
                                                      fill="none"
                                                      stroke="currentColor"
                                                      viewBox="0 0 24 24"
                                                    >
                                                      <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                                      />
                                                    </svg>
                                                  </button>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    )
                                  );
                                } catch (e) {
                                  return null;
                                }
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={!pagination.hasPrevPage}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      !pagination.hasPrevPage
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    First
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={!pagination.hasPrevPage}
                    className={`px-3 py-1 text-sm font-medium rounded-md flex items-center ${
                      !pagination.hasPrevPage
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Previous
                  </button>
                </div>

                <div className="flex items-center space-x-1">
                  {/* Page Numbers */}
                  {(() => {
                    const pages = [];
                    const totalPages = pagination.totalPages;
                    const current = pagination.currentPage;

                    // Show first page
                    if (current > 3) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => handlePageChange(1)}
                          className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                        >
                          1
                        </button>
                      );
                      if (current > 4) {
                        pages.push(
                          <span key="dots1" className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                    }

                    // Show pages around current
                    for (
                      let i = Math.max(1, current - 2);
                      i <= Math.min(totalPages, current + 2);
                      i++
                    ) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          className={`px-3 py-1 text-sm font-medium rounded-md ${
                            i === current
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {i}
                        </button>
                      );
                    }

                    // Show last page
                    if (current < totalPages - 2) {
                      if (current < totalPages - 3) {
                        pages.push(
                          <span key="dots2" className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => handlePageChange(totalPages)}
                          className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!pagination.hasNextPage}
                    className={`px-3 py-1 text-sm font-medium rounded-md flex items-center ${
                      !pagination.hasNextPage
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4 ml-1" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={!pagination.hasNextPage}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      !pagination.hasNextPage
                        ? "text-gray-400 cursor-not-allowed"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {investors.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            No investors found matching your criteria.
          </div>
        )}

        {/* Add/Edit Investor Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">
                    {editingInvestor ? "Edit Investor" : "Add New Investor"}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
                <InvestorForm
                  schemes={schemes}
                  onSubmit={handleFormSubmit}
                  initialData={editingInvestor}
                  onCancel={resetForm}
                />
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Referral Details Modal */}
        {showReferralModal && selectedReferralInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                      <svg
                        className="w-5 h-5 text-indigo-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Referral Details
                    </h2>
                  </div>
                  <button
                    onClick={() => setShowReferralModal(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Investor Name
                    </label>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-900 font-semibold">
                        {selectedReferralInfo.investor_name}
                      </p>
                      <button
                        onClick={() =>
                          copyToClipboard(selectedReferralInfo.investor_name)
                        }
                        className="text-gray-400 hover:text-blue-600 p-1 hover:bg-blue-50 rounded transition-colors duration-200"
                        title="Copy investor name"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <label className="block text-sm font-medium text-indigo-700 mb-2">
                      Referred By
                    </label>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-indigo-900 font-semibold">
                        {selectedReferralInfo.referred_by}
                      </p>
                      <button
                        onClick={() =>
                          copyToClipboard(selectedReferralInfo.referred_by)
                        }
                        className="text-indigo-400 hover:text-indigo-600 p-1 hover:bg-indigo-100 rounded transition-colors duration-200"
                        title="Copy referrer name"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <label className="block text-sm font-medium text-purple-700 mb-2">
                      Referrer Wallet Address
                    </label>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-purple-900 font-mono break-all mr-2">
                        {selectedReferralInfo.referrer_wallet || "N/A"}
                      </p>
                      {selectedReferralInfo.referrer_wallet && (
                        <button
                          onClick={() =>
                            copyToClipboard(
                              selectedReferralInfo.referrer_wallet
                            )
                          }
                          className="text-purple-400 hover:text-purple-600 p-1 hover:bg-purple-100 rounded transition-colors duration-200 flex-shrink-0"
                          title="Copy referrer wallet address"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-between items-center">
                  <button
                    onClick={() => {
                      const allDetails = `Investor: ${
                        selectedReferralInfo.investor_name
                      }\nReferred by: ${
                        selectedReferralInfo.referred_by
                      }\nReferrer Wallet: ${
                        selectedReferralInfo.referrer_wallet || "N/A"
                      }`;
                      copyToClipboard(allDetails);
                    }}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-100 rounded-lg hover:bg-indigo-200 transition-colors duration-200"
                  >
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Copy All Details
                  </button>
                  <button
                    onClick={() => setShowReferralModal(false)}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setRecordToDelete(null);
          }}
          onSoftDelete={handleSoftDelete}
          onPermanentDelete={handlePermanentDelete}
          itemName={recordToDelete?.buyer_name || "this record"}
          itemType="investor"
        />

        {/* Bulk Delete by Scheme Modal */}
        <BulkDeleteBySchemeModal
          isOpen={showBulkDeleteModal}
          onClose={() => setShowBulkDeleteModal(false)}
          schemes={schemes}
          onDeleteComplete={() => {
            fetchAllData();
            setShowBulkDeleteModal(false);
          }}
        />
      </div>
    </>
  );
};

export default memo(InvestorsPage);
